import {
  Button,
  Card,
  Division,
  Flex,
  Heading,
  Icon,
  Icons,
  Paragraph,
  Spin,
} from "@ir/ui";
import type { ReactNode } from "react";
import { Permissions } from "@src/domain/permissions";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import type { MasterDataSearchBySection } from "./masterDataSearchBy.config";
import { MASTER_DATA_SEARCH_BY_FIELDS } from "./masterDataSearchBy.config";
import { MasterDataSearchInput } from "./MasterDataSearchInput";

interface MasterDataWritableListLayoutProps {
  title: string;
  subtitle: string;
  searchPlaceholder: string;
  search: string;
  setSearch: (search: string) => void;
  searchSection: MasterDataSearchBySection;
  addButtonLabel: string;
  onAdd: () => void;
  headerActions?: ReactNode;
  hideSearch?: boolean;
  loading: boolean;
  children: ReactNode;
}

export const MasterDataWritableListLayout = ({
  title,
  subtitle,
  searchPlaceholder,
  search,
  setSearch,
  searchSection,
  addButtonLabel,
  onAdd,
  headerActions,
  hideSearch = false,
  loading,
  children,
}: MasterDataWritableListLayoutProps) => {
  return (
    <Card
      className="master-data-list-page bg-white"
      variant="outlined"
      shadow
      padding="small"
    >
      <Flex
        align="center"
        justifyContent="space-between"
        gap={0.75}
        className="master-data-list-page__header mb-3"
      >
        <Flex direction="column" gap={0.15}>
          <Heading level={6} className="text-grey-700 font-bold m-0">
            {title}
          </Heading>
          <Paragraph className="text-grey-500 m-0">{subtitle}</Paragraph>
        </Flex>
        <Flex align="center" gap={0.5}>
          {hideSearch ? null : (
            <MasterDataSearchInput
              placeholder={searchPlaceholder}
              searchQuery={search}
              onSearch={setSearch}
              onClear={() => setSearch("")}
              minLength={1}
              fields={MASTER_DATA_SEARCH_BY_FIELDS[searchSection]}
            />
          )}
          {headerActions}
          <HasAnyPermission
            requiredPermissions={[Permissions.MASTER_DATA_ADD]}
          >
            <Button size="small" variant="outline" onPress={onAdd}>
              <Icon slot="start" name={Icons.Plus} />
              {addButtonLabel}
            </Button>
          </HasAnyPermission>
        </Flex>
      </Flex>

      <Division className="master-data-list-page__body">
        {loading ? (
          <Flex align="center" justifyContent="center" className="py-6">
            <Spin tip="Loading..." spinning />
          </Flex>
        ) : (
          children
        )}
      </Division>
    </Card>
  );
};
